import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, History } from "lucide-react";

/**
 * Phase 3 — Admin financial approvals (mobile-first; works on desktop too).
 *
 * Aggregates pending proposals across all 5 kinds, lets the Admin / Director
 * approve or reject with remarks. Rejection requires a reason.
 */
const KINDS = [
  { key: "incentive_rule", label: "Incentive Rules" },
  { key: "target", label: "Targets" },
  { key: "slab", label: "Slabs" },
  { key: "bonus_rule", label: "Bonus Rules" },
  { key: "scheme", label: "Schemes" },
];

export default function AdminFinancialApprovals() {
  const [counts, setCounts] = useState({ total: 0 });
  const [rowsByKind, setRowsByKind] = useState({});
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // {kind, record, type}
  const [audit, setAudit] = useState(null);
  const [details, setDetails] = useState(null);   // {kind, record}

  const load = async () => {
    setLoading(true);
    try {
      const overview = await api.get("/promoter/financial/_overview/pending");
      setCounts(overview.data);
      const map = {};
      for (const k of KINDS) {
        const { data } = await api.get(`/promoter/financial/${k.key}`, { params: { status: "pending_admin_approval" } });
        map[k.key] = data || [];
      }
      setRowsByKind(map);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="admin-fin-approvals-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Financial Approvals</h1>
          <p className="text-sm text-slate-500 mt-1">
            Review Product Manager proposals. Records have no effect until approved.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="bg-amber-100 text-amber-800 text-sm" data-testid="fin-pending-total">
            {counts.total || 0} pending
          </Badge>
          <Button variant="outline" onClick={load} data-testid="fin-approvals-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </header>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && (counts.total || 0) === 0 && (
        <Card>
          <CardContent className="p-8 text-center text-sm text-slate-500" data-testid="fin-approvals-empty">
            No pending financial proposals right now.
          </CardContent>
        </Card>
      )}

      {!loading && KINDS.map((k) => {
        const rows = rowsByKind[k.key] || [];
        if (rows.length === 0) return null;
        return (
          <Card key={k.key} data-testid={`fin-approval-group-${k.key}`}>
            <CardContent className="p-4 space-y-3">
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                {k.label}
                <Badge className="bg-slate-200 text-slate-700">{rows.length}</Badge>
              </div>
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.id} className="border border-slate-100 rounded p-3 flex flex-wrap gap-2 items-center"
                       data-testid={`fin-approval-row-${r.id.slice(0, 8)}`}>
                    <div className="flex-1 min-w-[260px]">
                      <ProposalSummary kind={k.key} payload={r.payload} />
                      <div className="text-[10px] text-slate-500 mt-1">
                        by {r.created_by || "—"} · submitted {(r.submitted_at || "").slice(0, 19)}
                        {r.revision_of ? ` · revision of ${r.revision_of.slice(0, 8)}…` : ""}
                      </div>
                      {r.remarks && <div className="text-xs text-slate-600 mt-0.5">Note: {r.remarks}</div>}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setDetails({ kind: k.key, record: r })}
                            data-testid={`fin-approval-view-${r.id.slice(0, 8)}`}>
                      View Details
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setAudit({ kind: k.key, record: r })}
                            data-testid={`fin-approval-audit-${r.id.slice(0, 8)}`}>
                      <History size={12} />
                    </Button>
                    <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white"
                            onClick={() => setDecision({ kind: k.key, record: r, type: "approve" })}
                            data-testid={`fin-approval-approve-${r.id.slice(0, 8)}`}>
                      <CheckCircle2 size={12} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="text-rose-700 hover:bg-rose-50"
                            onClick={() => setDecision({ kind: k.key, record: r, type: "reject" })}
                            data-testid={`fin-approval-reject-${r.id.slice(0, 8)}`}>
                      <XCircle size={12} className="mr-1" /> Reject
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <FinDecisionDialog state={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); }} />
      <AuditDialog state={audit} onClose={() => setAudit(null)} />
      <FinDetailsDialog state={details} onClose={() => setDetails(null)}
                        onApprove={(rec) => { setDetails(null); setDecision({ ...rec, type: "approve" }); }}
                        onReject={(rec) => { setDetails(null); setDecision({ ...rec, type: "reject" }); }} />
    </div>
  );
}

/** Iter42 — full View Details modal (scrollable). Renders per-kind content. */
function FinDetailsDialog({ state, onClose, onApprove, onReject }) {
  if (!state) return null;
  const { kind, record } = state;
  const p = record.payload || {};
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="fin-details-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
          <DialogTitle>View details — {KIND_LABEL[kind] || kind}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 text-sm" data-testid="fin-details-scroll">
          <KV label="Submitted by" value={record.created_by || "—"} />
          <KV label="Submitted at" value={(record.submitted_at || "").slice(0, 19)} />
          {record.revision_of && <KV label="Revision of" value={record.revision_of} />}
          {record.remarks && <KV label="Maker remarks" value={record.remarks} />}
          <div className="border-t border-slate-100 pt-2">
            {kind === "incentive_rule" && (
              <>
                <KV label="Brand" value={p.brand} />
                <KV label="Category" value={p.product_category || "—"} />
                <KV label="Model" value={p.model || "—"} />
                <KV label="Audience" value={p.audience || "regular_promoter"} />
                <KV label="Incentive amount" value={`Rs. ${Number(p.incentive_amount || 0).toLocaleString()} / unit`} />
                <KV label="Effective from (AD)" value={p.effective_from || p.start_date || "—"} />
                <KV label="Effective to (AD)" value={p.effective_to || p.end_date || "open-ended"} />
              </>
            )}
            {kind === "target" && (
              <>
                <KV label="Promoter ID" value={p.promoter_id} />
                <KV label="BS Month / Year" value={p.bs_period_label ? `${p.bs_period_label} (FY ${p.bs_fiscal_year || "—"})` : "—"} />
                <KV label="BS Start Date" value={p.bs_start || "—"} />
                <KV label="BS End Date" value={p.bs_end || "—"} />
                <KV label="AD Start Date (ref)" value={p.bs_ad_start || "—"} />
                <KV label="AD End Date (ref)" value={p.bs_ad_end || "—"} />
                <KV label="AD Month / Year (key)" value={p.month_year} />
                <KV label="Target sales" value={`Rs. ${Number(p.target_sales_amount || 0).toLocaleString()}`} />
                {p.target_quantity ? <KV label="Target qty" value={`${p.target_quantity} units`} /> : null}
              </>
            )}
            {kind === "slab" && (
              <>
                <KV label="Achievement %" value={`${p.achievement_pct_from}%${p.achievement_pct_to ? `–${p.achievement_pct_to}%` : "+"}`} />
                <KV label="Payout %" value={`${p.payout_pct}%`} />
                {p.bonus_type && p.bonus_type !== "none" && (
                  <KV label="Bonus" value={`${p.bonus_type} ${p.bonus_value}`} />
                )}
              </>
            )}
            {kind === "bonus_rule" && (
              <>
                <KV label="Name" value={p.name} />
                <KV label="Bonus amount" value={`Rs. ${Number(p.bonus_amount || 0).toLocaleString()}`} />
                <KV label="Condition" value={p.condition || "—"} />
              </>
            )}
            {kind === "scheme" && (
              <>
                <KV label="Name" value={p.name} />
                <KV label="Target type" value={p.target_type} />
                <KV label="Dealer" value={p.dealer_name || p.dealer_id || "All dealers"} />
                <KV label="Product / Model" value={p.product_model || p.product_category || "Any"} />
                <KV label="BS Period" value={p.bs_period_label || "—"} />
                {p.bs_start && <KV label="BS Start Date" value={p.bs_start} />}
                {p.bs_end && <KV label="BS End Date" value={p.bs_end} />}
                <KV label="AD Start Date (ref)" value={p.start_date || "—"} />
                <KV label="AD End Date (ref)" value={p.end_date || "—"} />
                <KV label="Target amount" value={`Rs. ${Number(p.target_amount || 0).toLocaleString()}`} />
                {p.dealer_benefit && <KV label="Dealer benefit" value={p.dealer_benefit} />}
                {p.self_promoter_entry_incentive && <KV label="Self-promoter entry incentive" value={p.self_promoter_entry_incentive} />}
              </>
            )}
          </div>
          {record.audit_trail && record.audit_trail.length > 0 && (
            <div className="border-t border-slate-100 pt-2">
              <div className="text-[11px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Audit trail</div>
              {record.audit_trail.map((a, i) => (
                <div key={i} className="text-xs text-slate-600 font-mono">
                  {(a.at || "").slice(0, 19)} · {a.by || "—"} · {a.action || ""} {a.reason ? `· ${a.reason}` : ""}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t border-slate-100 flex-shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="fin-details-close">Close</Button>
          <Button variant="outline" className="text-rose-700 hover:bg-rose-50"
                  onClick={() => onReject({ kind, record })}
                  data-testid="fin-details-reject">
            <XCircle size={12} className="mr-1" /> Reject
          </Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  onClick={() => onApprove({ kind, record })}
                  data-testid="fin-details-approve">
            <CheckCircle2 size={12} className="mr-1" /> Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const KIND_LABEL = {
  incentive_rule: "Incentive Rule",
  target: "Target",
  slab: "Slab",
  bonus_rule: "Bonus Rule",
  scheme: "Sell-out Scheme",
};

function KV({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1 border-b border-slate-50">
      <div className="col-span-1 text-xs text-slate-500 uppercase font-semibold tracking-wider">{label}</div>
      <div className="col-span-2 text-sm text-slate-900 break-words">{value ?? "—"}</div>
    </div>
  );
}

function ProposalSummary({ kind, payload }) {
  if (!payload) return null;
  switch (kind) {
    case "incentive_rule":
      return <div className="text-sm text-slate-900"><b>{payload.brand}</b>{payload.product_category ? ` / ${payload.product_category}` : ""}{payload.model ? ` / ${payload.model}` : ""} → Rs. {payload.incentive_amount}/unit</div>;
    case "target":
      return <div className="text-sm text-slate-900">{payload.month_year} · Promoter {payload.promoter_id?.slice(0, 8)}… · Rs. {Number(payload.target_sales_amount).toLocaleString()}{payload.target_quantity ? ` · ${payload.target_quantity} units` : ""}</div>;
    case "slab":
      return <div className="text-sm text-slate-900">{payload.achievement_pct_from}%{payload.achievement_pct_to ? `–${payload.achievement_pct_to}%` : "+"} → {payload.payout_pct}% payout{payload.bonus_type && payload.bonus_type !== "none" ? ` + ${payload.bonus_type} ${payload.bonus_value}` : ""}</div>;
    case "bonus_rule":
      return <div className="text-sm text-slate-900"><b>{payload.name}</b> · Rs. {payload.bonus_amount}</div>;
    case "scheme":
      return <div className="text-sm text-slate-900"><b>{payload.name}</b> · {payload.target_type} · {payload.start_date}→{payload.end_date} · Rs. {Number(payload.target_amount).toLocaleString()}</div>;
    default:
      return <div className="text-sm text-slate-900">{JSON.stringify(payload)}</div>;
  }
}

function FinDecisionDialog({ state, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReason(""); setRemarks(""); }, [state]);

  if (!state) return null;
  const isApprove = state.type === "approve";

  const submit = async () => {
    if (!isApprove && !reason.trim()) { toast.error("Rejection reason is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/promoter/financial/${state.kind}/${state.record.id}/decide`, {
        decision: isApprove ? "approved" : "rejected",
        remarks: remarks || null,
        rejection_reason: isApprove ? null : reason,
      });
      toast.success(isApprove ? "Approved" : "Rejected");
      onDone();
    } catch (e) {
      toast.error("Could not decide", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`fin-decide-dialog-${state.type}`}>
        <DialogHeader>
          <DialogTitle>{isApprove ? "Approve" : "Reject"} · {state.kind.replace("_", " ")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <ProposalSummary kind={state.kind} payload={state.record.payload} />
          {!isApprove && (
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-semibold text-slate-600">
                Rejection reason <span className="text-rose-600">*</span>
              </Label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        data-testid="fin-decide-reason" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks (optional)</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="fin-decide-remarks" />
          </div>
          {isApprove && (
            <div className="text-[11px] text-slate-500">
              Once approved, this becomes Active and is consumed by the calculation engine.
              You can deactivate it later from the Financial Rules screen.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="fin-decide-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className={isApprove ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-rose-700 hover:bg-rose-800 text-white"}
                  data-testid="fin-decide-confirm">
            {busy ? "Saving..." : `Confirm ${isApprove ? "approve" : "reject"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditDialog({ state, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!state) return;
    api.get(`/promoter/financial/_audit/${state.record.id}`).then(({ data }) => setRows(data)).catch(() => setRows([]));
  }, [state]);
  if (!state) return null;
  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="fin-approval-audit-dialog">
        <DialogHeader><DialogTitle>Audit trail</DialogTitle></DialogHeader>
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {rows.length === 0 && <div className="text-sm text-slate-400">No audit entries.</div>}
          {rows.map((r) => (
            <div key={r.id} className="border border-slate-100 rounded p-2 text-sm">
              <div className="font-medium">{r.action}</div>
              <div className="text-xs text-slate-600">by {r.actor_name} ({r.actor_role}) · {(r.ts || "").slice(0, 19)}</div>
              {r.remarks && <div className="text-xs text-slate-500 mt-1">Remarks: {r.remarks}</div>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
